import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Trash2, ShoppingBag } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { useWishlist } from "../context/WishlistContext";
import { useCart } from "../context/CartContext";
import { formatPrice } from "../lib/format";
import Stars from "../components/Stars";
import { toast } from "sonner";

export default function Wishlist() {
  const { ids, remove, clear } = useWishlist();
  const { add } = useCart();
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (ids.length === 0) {
      setProducts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all(
      ids.map((id) => api.get(`/products/${id}`).then((r) => r.data).catch(() => null))
    )
      .then((list) => setProducts(list.filter(Boolean)))
      .finally(() => setLoading(false));
  }, [ids]);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12" data-testid="wishlist-page">
      <div className="border-b border-white/10 pb-8 mb-12 flex items-end justify-between gap-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#C5CDD8]">
            {t("wishlist.title")}
          </div>
          <h1 className="font-display mt-3 text-4xl sm:text-5xl font-bold uppercase tracking-tighter text-white">
            {t("wishlist.title")}
          </h1>
          <div className="mt-3 text-sm text-white/60">
            {products.length} {products.length > 1 ? t("wishlist.saved_plural") : t("wishlist.saved_singular")}
          </div>
        </div>
        {products.length > 0 && (
          <button
            onClick={() => {
              if (confirm(t("wishlist.clear_confirm"))) {
                clear();
                toast.success(t("wishlist.cleared"));
              }
            }}
            className="text-xs font-bold uppercase tracking-[0.25em] text-white/60 hover:text-white underline underline-offset-4"
            data-testid="wishlist-clear"
          >
            {t("wishlist.clear_all")}
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-24 text-sm text-white/60">{t("common.loading")}</div>
      ) : products.length === 0 ? (
        <div className="text-center py-24" data-testid="wishlist-empty">
          <Heart size={48} strokeWidth={1} className="mx-auto text-white/30" />
          <p className="mt-6 text-sm text-white/60">
            {t("wishlist.empty")}
          </p>
          <Link
            to="/shop"
            className="mt-8 inline-block border border-white bg-white text-[#001F3F] hover:bg-transparent hover:text-white transition-all uppercase tracking-[0.25em] text-xs font-bold py-4 px-8"
          >
            {t("wishlist.empty_cta")}
          </Link>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="wishlist-list">
          {products.map((p) => (
            <article
              key={p.id}
              className="border border-white/10 bg-[#0A2A4F] overflow-hidden group"
              data-testid={`wishlist-item-${p.id}`}
            >
              <Link to={`/product/${p.id}`} className="block aspect-[4/3] overflow-hidden">
                <img
                  src={p.image}
                  alt={p.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
              </Link>
              <div className="p-5">
                <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#C5CDD8]">
                  {p.category === "smart" ? "Smart Tech" : "Wellness"}
                </div>
                <Link
                  to={`/product/${p.id}`}
                  className="block mt-1 text-base font-medium text-white hover:underline underline-offset-4 truncate"
                >
                  {p.name}
                </Link>
                {p.review_count > 0 && (
                  <div className="mt-2 flex items-center gap-1.5">
                    <Stars value={p.avg_rating} size={11} />
                    <span className="text-[10px] text-white/50">({p.review_count})</span>
                  </div>
                )}
                <div className="mt-4 flex items-center justify-between">
                  <div className="text-lg font-bold text-white">{formatPrice(p.price)}</div>
                  <button
                    onClick={() => {
                      remove(p.id);
                      toast.success(t("wishlist.removed", { name: p.name }));
                    }}
                    aria-label={t("common.delete")}
                    className="p-2 text-white/50 hover:text-white"
                    data-testid={`wishlist-remove-${p.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                <button
                  onClick={() => {
                    add(p, 1);
                    toast.success(t("wishlist.added_to_cart", { name: p.name }));
                  }}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 border border-white bg-transparent text-white hover:bg-white hover:text-[#001F3F] transition-all uppercase tracking-[0.25em] text-[10px] font-bold py-3"
                  data-testid={`wishlist-add-cart-${p.id}`}
                >
                  <ShoppingBag size={12} /> {t("common.add_to_cart")}
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
